import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  RefreshCw,
  Play,
  XCircle,
  Loader2,
  Home,
  ExternalLink,
  AlertTriangle,
  Trash2,
  Eye,
  TestTube,
  Zap
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

const statusConfig = {
  pending: { label: "Pending", color: "bg-slate-500", icon: Clock },
  processing: { label: "Processing", color: "bg-blue-500", icon: Loader2 },
  succeeded: { label: "Succeeded", color: "bg-green-500", icon: CheckCircle },
  failed: { label: "Failed", color: "bg-red-500", icon: XCircle },
  retrying: { label: "Retrying", color: "bg-amber-500", icon: RefreshCw },
  permanently_failed: { label: "Dead Letter", color: "bg-red-700", icon: AlertTriangle },
};

const portalConfig = {
  RIGHTMOVE: { color: "bg-blue-600", name: "Rightmove" },
  ZOOPLA: { color: "bg-purple-600", name: "Zoopla" },
  ONTHEMARKET: { color: "bg-green-600", name: "OnTheMarket" },
};

export default function PublishingJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [portalStatus, setPortalStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState("jobs");
  const [smokeTestResults, setSmokeTestResults] = useState(null);
  const [runningTest, setRunningTest] = useState(false);
  const [processingJobs, setProcessingJobs] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchJobs = useCallback(async () => {
    try {
      const params = { page, page_size: 20 };
      if (statusFilter && statusFilter !== "all") {
        params.status = statusFilter;
      }
      const response = await axios.get(`${API}/portal-publishing/jobs`, { headers, params });
      setJobs(response.data.jobs);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Failed to fetch publishing jobs");
    }
  }, [page, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portal-publishing/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  const fetchPortalStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portal-publishing/status`, { headers });
      setPortalStatus(response.data);
    } catch (error) {
      console.error("Error fetching portal status:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchJobs(), fetchStats(), fetchPortalStatus()]);
      setLoading(false);
    };
    loadData();
  }, [fetchJobs, fetchStats, fetchPortalStatus]);

  const handleRerunJob = async (jobId) => {
    try {
      await axios.post(`${API}/portal-publishing/jobs/${jobId}/rerun`, {}, { headers });
      toast.success("Job queued for re-run");
      fetchJobs();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rerun job");
    }
  };

  const handleProcessPending = async () => {
    setProcessingJobs(true);
    try {
      const response = await axios.post(`${API}/portal-publishing/process-pending`, {}, { headers });
      toast.success(`Processed ${response.data.processed} jobs`);
      fetchJobs();
      fetchStats();
    } catch (error) {
      toast.error("Failed to process jobs");
    } finally {
      setProcessingJobs(false);
    }
  };

  const handleRunSmokeTest = async (portal = null) => {
    setRunningTest(true);
    try {
      const params = portal ? { portal } : {};
      const response = await axios.post(`${API}/portal-publishing/smoke-test`, null, { headers, params });
      setSmokeTestResults(response.data);
      if (response.data.overall_status === "PASS") {
        toast.success("Smoke test passed!");
      } else {
        toast.warning("Smoke test completed with failures");
      }
    } catch (error) {
      toast.error("Smoke test failed to run");
    } finally {
      setRunningTest(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const PortalBadge = ({ portal }) => {
    const config = portalConfig[portal] || { color: "bg-slate-500", name: portal };
    return (
      <Badge className={`${config.color} text-white`}>
        {config.name}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="publishing-jobs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Portal Publishing</h1>
          <p className="text-slate-500">Manage property listings on Rightmove, Zoopla, and OnTheMarket</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleProcessPending}
            disabled={processingJobs}
            data-testid="process-pending-btn"
          >
            {processingJobs ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Process Pending
          </Button>
          <Button
            onClick={() => handleRunSmokeTest()}
            disabled={runningTest}
            data-testid="smoke-test-btn"
          >
            {runningTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
            Run Smoke Test
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.total_jobs}</div>
              <div className="text-xs text-slate-500">Total Jobs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-600">{stats.pending}</div>
              <div className="text-xs text-slate-500">Pending</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-600">{stats.processing}</div>
              <div className="text-xs text-slate-500">Processing</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">{stats.succeeded}</div>
              <div className="text-xs text-slate-500">Succeeded</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-amber-600">{stats.retrying}</div>
              <div className="text-xs text-slate-500">Retrying</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
              <div className="text-xs text-slate-500">Failed</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-800">{stats.dead_letter_count}</div>
              <div className="text-xs text-slate-500">Dead Letter</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Portal Status */}
      {portalStatus && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Portal Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              {Object.entries(portalStatus.portals).map(([portal, status]) => (
                <div key={portal} className="flex items-center gap-2">
                  <PortalBadge portal={portal} />
                  {status.configured ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-sm text-slate-600">
                    {status.configured ? "Configured" : "Not Configured"}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="jobs">Publishing Jobs</TabsTrigger>
          <TabsTrigger value="smoke-test">Smoke Test Results</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Publishing Jobs</CardTitle>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="succeeded">Succeeded</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="retrying">Retrying</SelectItem>
                    <SelectItem value="permanently_failed">Dead Letter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job ID</TableHead>
                    <TableHead>Instruction</TableHead>
                    <TableHead>Portals</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Attempts</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell className="font-mono text-xs">{job.id}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Home className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{job.instruction_id}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {job.targets?.map((t) => (
                            <PortalBadge key={t} portal={t.toUpperCase()} />
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={job.status} />
                      </TableCell>
                      <TableCell>{job.attempts || 0}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(job.created_at).toLocaleString("en-GB")}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedJob(job)}
                            data-testid={`view-job-${job.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {(job.status === "failed" || job.status === "permanently_failed") && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRerunJob(job.id)}
                              data-testid={`rerun-job-${job.id}`}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {jobs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        No publishing jobs found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span className="px-4 py-2 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="smoke-test">
          <Card>
            <CardHeader>
              <CardTitle>Smoke Test Results</CardTitle>
              <CardDescription>
                Test portal integrations with live credentials
              </CardDescription>
            </CardHeader>
            <CardContent>
              {smokeTestResults ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Badge className={smokeTestResults.overall_status === "PASS" ? "bg-green-500" : "bg-red-500"}>
                      {smokeTestResults.overall_status}
                    </Badge>
                    <span className="text-sm text-slate-500">
                      Test ID: {smokeTestResults.test_id}
                    </span>
                    <span className="text-sm text-slate-500">
                      {new Date(smokeTestResults.timestamp).toLocaleString("en-GB")}
                    </span>
                  </div>

                  <div className="grid gap-4">
                    {Object.entries(smokeTestResults.portals).map(([portal, result]) => (
                      <Card key={portal}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-4">
                            <PortalBadge portal={portal} />
                            {result.configured ? (
                              <Badge className="bg-green-100 text-green-700">Configured</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700">Not Configured</Badge>
                            )}
                          </div>
                          
                          {result.error ? (
                            <div className="text-red-600 text-sm">{result.error}</div>
                          ) : (
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-slate-500">Create:</span>
                                {result.create?.success ? (
                                  <CheckCircle className="inline w-4 h-4 ml-1 text-green-500" />
                                ) : (
                                  <XCircle className="inline w-4 h-4 ml-1 text-red-500" />
                                )}
                              </div>
                              <div>
                                <span className="text-slate-500">Update:</span>
                                {result.update?.success ? (
                                  <CheckCircle className="inline w-4 h-4 ml-1 text-green-500" />
                                ) : (
                                  <XCircle className="inline w-4 h-4 ml-1 text-red-500" />
                                )}
                              </div>
                              <div>
                                <span className="text-slate-500">Deactivate:</span>
                                {result.deactivate?.success ? (
                                  <CheckCircle className="inline w-4 h-4 ml-1 text-green-500" />
                                ) : (
                                  <XCircle className="inline w-4 h-4 ml-1 text-red-500" />
                                )}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <TestTube className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No smoke test results yet</p>
                  <p className="text-sm">Click &quot;Run Smoke Test&quot; to test portal integrations</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Job Details Dialog */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Job Details</DialogTitle>
            <DialogDescription>
              {selectedJob?.id}
            </DialogDescription>
          </DialogHeader>
          
          {selectedJob && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-500">Status</label>
                  <div><StatusBadge status={selectedJob.status} /></div>
                </div>
                <div>
                  <label className="text-sm text-slate-500">Attempts</label>
                  <div className="font-medium">{selectedJob.attempts || 0}</div>
                </div>
                <div>
                  <label className="text-sm text-slate-500">Created</label>
                  <div className="text-sm">{new Date(selectedJob.created_at).toLocaleString("en-GB")}</div>
                </div>
                <div>
                  <label className="text-sm text-slate-500">Completed</label>
                  <div className="text-sm">
                    {selectedJob.completed_at ? new Date(selectedJob.completed_at).toLocaleString("en-GB") : "-"}
                  </div>
                </div>
              </div>

              {selectedJob.last_error && (
                <div>
                  <label className="text-sm text-slate-500">Last Error</label>
                  <div className="p-3 bg-red-50 rounded-lg text-red-700 text-sm">
                    {selectedJob.last_error}
                  </div>
                </div>
              )}

              {selectedJob.result && (
                <div>
                  <label className="text-sm text-slate-500">Portal Results</label>
                  <div className="space-y-2 mt-2">
                    {Object.entries(selectedJob.result).map(([portal, result]) => (
                      <div key={portal} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <PortalBadge portal={portal} />
                          {result.success ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                        {result.listing_url && (
                          <a href={result.listing_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm flex items-center gap-1">
                            View <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        {result.error && (
                          <span className="text-red-600 text-sm">{result.error}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {selectedJob && (selectedJob.status === "failed" || selectedJob.status === "permanently_failed") && (
              <Button onClick={() => { handleRerunJob(selectedJob.id); setSelectedJob(null); }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Re-run Job
              </Button>
            )}
            <Button variant="outline" onClick={() => setSelectedJob(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
